import { Link, useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { formatMinor, useShifts, type ShiftState, type ShiftSummary } from '@/hooks/use-shifts';
import { useT } from '@/i18n/use-t';

/**
 * The states this page offers as filter chips. It is also the allow-list for
 * `?state=` — anything else in the URL falls back to `all` rather than being
 * passed through to the API as an unfilterable value.
 */
const URL_STATES: readonly ShiftState[] = [
  'open',
  'active',
  'closing',
  'closed',
  'reconciled',
] as const;

const TRADING_STATES: readonly ShiftState[] = ['open', 'active', 'paused', 'closing'];

function stateFromUrl(raw: string | null): ShiftState | 'trading' | 'all' {
  if (raw === null) return 'all';
  if (raw === 'trading') return raw;
  const match = URL_STATES.find((s) => s === raw);
  return match ?? 'all';
}

export function ShiftsListPage() {
  const t = useT('admin_ui');
  /**
   * RL-377: the state filter lives in the URL so the dashboard's open-shifts
   * tile can deep-link to `/shifts?state=open`, and so refresh, sharing and
   * browser Back all preserve the drill-down.
   */
  const [params, setParams] = useSearchParams();
  const state = stateFromUrl(params.get('state'));
  const setState = (next: ShiftState | 'trading' | 'all'): void => {
    setParams(next === 'all' ? {} : { state: next });
  };
  const { data, isLoading, error } = useShifts({
    pageSize: 50,
    state: state === 'all' ? undefined : state === 'trading' ? TRADING_STATES : state,
  });

  const stateFilters: { label: string; value: ShiftState | 'trading' | 'all' }[] = [
    { label: t('shifts.list.filter.all', 'All'), value: 'all' },
    { label: t('shifts.list.filter.trading', 'Trading'), value: 'trading' },
    { label: t('shifts.list.filter.open', 'Open'), value: 'open' },
    { label: t('shifts.list.filter.active', 'Active'), value: 'active' },
    { label: t('shifts.list.filter.closing', 'Closing'), value: 'closing' },
    { label: t('shifts.list.filter.closed', 'Closed'), value: 'closed' },
    { label: t('shifts.list.filter.reconciled', 'Reconciled'), value: 'reconciled' },
  ];

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">{t('shifts.list.title', 'Shifts')}</h1>
        {/* Shifts are authored on the terminals and uploaded through
            `/sync/shift-events`; the endpoint name is not operator copy (RL-331). */}
        <p className="text-sm text-muted-foreground">
          {data === undefined
            ? '…'
            : t(
                'shifts.list.intro',
                '{count} shift(s). Shifts are opened and closed on the terminals and appear here automatically; reconcile them here after closing.',
              ).replace('{count}', String(data.total))}
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-md border border-border bg-card p-1 text-sm">
          {stateFilters.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setState(opt.value)}
              className={
                state === opt.value
                  ? 'rounded bg-primary px-3 py-1 text-primary-foreground'
                  : 'rounded px-3 py-1 text-muted-foreground hover:bg-secondary'
              }
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-md border border-border bg-card">
        <Table>
          <THead>
            <TR>
              <TH>{t('shifts.field.opened', 'Opened')}</TH>
              <TH>{t('shifts.field.terminal', 'Terminal')}</TH>
              <TH>{t('shifts.field.cashier', 'Cashier')}</TH>
              <TH>{t('shifts.field.state', 'State')}</TH>
              <TH className="text-right">{t('shifts.field.opening', 'Opening')}</TH>
              <TH className="text-right">{t('shifts.list.colClosing', 'Closing (cnt / exp)')}</TH>
              <TH className="text-right">{t('shifts.field.variance', 'Variance')}</TH>
              <TH className="w-24" />
            </TR>
          </THead>
          <TBody>
            {isLoading && (
              <TR>
                <TD colSpan={8} className="py-8 text-center text-muted-foreground">
                  {t('common.loading', 'Loading…')}
                </TD>
              </TR>
            )}
            {error !== null && (
              <TR>
                <TD colSpan={8} className="py-8 text-center text-destructive">
                  {t('shifts.list.loadFailed', 'Failed to load shifts')}
                </TD>
              </TR>
            )}
            {data?.shifts.length === 0 && (
              <TR>
                <TD colSpan={8} className="py-8 text-center text-muted-foreground">
                  {t('shifts.list.empty', 'No shifts in this view yet. Open one from a terminal.')}
                </TD>
              </TR>
            )}
            {data?.shifts.map((s) => (
              <TR key={s.id}>
                <TD className="text-xs">{formatDateTime(s.openedAt)}</TD>
                <TD className="font-mono text-xs">{s.terminalId.slice(0, 8)}…</TD>
                <TD className="font-mono text-xs">{s.cashierId.slice(0, 8)}…</TD>
                <TD>
                  <StateBadge state={s.state} />
                </TD>
                <TD className="text-right tabular-nums">
                  {formatMinor(s.openingCashMinor, s.currencyCode)}
                </TD>
                <TD className="text-right tabular-nums">
                  {s.closingCashCountedMinor === null
                    ? '—'
                    : `${formatMinor(s.closingCashCountedMinor, s.currencyCode)} / ${formatMinor(
                        s.closingCashExpectedMinor,
                        s.currencyCode,
                      )}`}
                </TD>
                <TD className="text-right tabular-nums">
                  <VarianceCell value={s.varianceMinor} currency={s.currencyCode} />
                </TD>
                <TD className="text-right">
                  <Link to={`/shifts/${s.id}`} className="text-sm text-primary hover:underline">
                    {t('shifts.action.open', 'Open')}
                  </Link>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </div>
  );
}

function StateBadge({ state }: { state: ShiftSummary['state'] }) {
  const t = useT('admin_ui');
  switch (state) {
    case 'open':
      return <Badge variant="success">{t('shifts.state.open', 'open')}</Badge>;
    case 'active':
      return <Badge variant="success">{t('shifts.state.active', 'active')}</Badge>;
    case 'closing':
      return <Badge variant="outline">{t('shifts.state.closing', 'closing')}</Badge>;
    case 'closed':
      return <Badge variant="outline">{t('shifts.state.closed', 'closed')}</Badge>;
    case 'reconciled':
      return <Badge variant="success">{t('shifts.state.reconciled', 'reconciled')}</Badge>;
    case 'paused':
      return <Badge variant="outline">{t('shifts.state.paused', 'paused')}</Badge>;
    case 'abnormal_close':
      return (
        <Badge variant="destructive">{t('shifts.state.abnormalClose', 'abnormal close')}</Badge>
      );
  }
}

function VarianceCell({ value, currency }: { value: string | null; currency: string | null }) {
  if (value === null) return <span className="text-muted-foreground">—</span>;
  const bi = BigInt(value);
  if (bi === 0n)
    return <span className="text-muted-foreground">{formatMinor(value, currency)}</span>;
  return (
    <span className={bi < 0n ? 'text-destructive' : 'text-primary'}>
      {formatMinor(value, currency)}
    </span>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}
